import {ICellRendererAngularComp} from "ag-grid-angular";
import {IAfterGuiAttachedParams, ICellRendererParams} from 'ag-grid-community';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component} from "@angular/core";
import {
    DetectMethodChanges,
    isValidNumber,
    isVoid,
} from "../../utils";
import {HedgeMatrixRow, HgHedgeMatrixComponent} from "./hg-hedge-matrix.component";
import {HedgeData} from "./hedge-data";

import {OptionsChainService} from "../../option-chains.service";

interface ComponentParams {
    comp: HgHedgeMatrixComponent;
    hedge: HedgeData;
    expiration: string;
    side: string;
    allInclusive: boolean;
    underlying?: string;
}

@Component({
    selector: "ets-expiration-pnl-cell-renderer",
    template: `
      <div class="cell">
        <span *ngIf="this.hasTransactions" class="cost"
              [ngStyle]="{ 'color': this.transCost < 0 ? 'red' : (this.transCost > 0 ? 'green' : undefined) }"
              (dblclick)="this.showAlert('trans')">
          {{ this.transCost | currency:'USD':'symbol':'1.2-2' }}
        </span>
        <span *ngIf="this.showSeparator" class="separator">&nbsp;|&nbsp;</span>
        <span *ngIf="this.hasOriginal" class="cost"
              [ngStyle]="{ 'color': this.originalCost < 0 ? 'red' : (this.originalCost > 0 ? 'green' : undefined) }"
              (dblclick)="this.showAlert('original')">
          {{ this.originalCost | currency:'USD':'symbol':'1.2-2' }}
        </span>
      </div>
    `,
    styles: [`
        .cell {
            display: flex;
            justify-content: center;
            align-items: center;
        }

        .cost {
            text-align: center;
            cursor: pointer;
        }

        .separator {
            font-size: 1.1em;
            color: white;
        }
    `],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExpirationPnlCellRenderer implements ICellRendererAngularComp {

    constructor(
        private _changeDetector: ChangeDetectorRef,
        private _optionChainService: OptionsChainService
    ) {
    }


    private _params: (ICellRendererParams & ComponentParams);

    value: number;
    formatValue: string;

    originalCost: number;
    transCost: number;

    get hasTransactions(): boolean {
        return isValidNumber(this.transCost);
    }

    get hasOriginal(): boolean {
        return isValidNumber(this.originalCost);
    }

    get showSeparator() : boolean {
        return this.hasOriginal && this.hasTransactions;
    }

    @DetectMethodChanges()
    agInit(params: (ICellRendererParams & ComponentParams)): void {
        this._params = params;
    }

    @DetectMethodChanges()
    refresh(): boolean {

        try {

            const params = this._params;

            if (isVoid(params)) return false;

            const data: HedgeMatrixRow = params.data;

            if (isVoid(data)) return false;

            const expiration = params.expiration;

            const side = params.side;

            if (isVoid(expiration)) return false;

            const comp = params.comp;

            if (isVoid(comp)) return false;

            const allInclusive = params.allInclusive;
            const underlying = params.underlying;

            const hedgePool = underlying
                ? comp.hedgeMatrixDataService.getHedgesByUnderlying(underlying)
                : comp.hedgeMatrixDataService.getHedges();

            let visible = allInclusive
                ? hedgePool.map(x => x.id)
                : comp.getVisibleHedges().filter(id => hedgePool.some(h => h.id === id));

            const hedgeIds = hedgePool
                .filter(x => visible.indexOf(x.id) >= 0)
                .filter(x => isVoid(side) ? true : x.type === side)
                .filter(x =>
                    comp.hedgeMatrixDataService.hedgeContainsExpiration(x.id, expiration));

            let originalPnl;
            let transPnl;

            hedgeIds.forEach(x => {

                const originalCost = comp.hedgeMatrixDataService.getOriginalCost(
                    x.id,
                    expiration
                ) || 0;

                if (isValidNumber(originalCost, true)) {
                    if (originalPnl === undefined) {
                        originalPnl = 0;
                    }
                    originalPnl += originalCost;
                }

                const transCost = comp.hedgeMatrixDataService.getTransCost(
                    x.id,
                    expiration
                ) || 0;

                if (isValidNumber(transCost, true)) {
                    if (transPnl === undefined) {
                        transPnl = 0;
                    }
                    transPnl += transCost;
                }
            });

            this.transCost = transPnl;
            this.originalCost = originalPnl;

        } catch {
        }

        return true;
    }

    @DetectMethodChanges()
    afterGuiAttached(params?: IAfterGuiAttachedParams) {
    }

    showAlert(legType: 'original' | 'trans' | 'outcome'): void {
        const expiration = this._params.expiration;
        const side = this._params.side;
        setTimeout(() => {
            this._params.comp.showPricingSteps('total', legType,
                expiration, side);
        });
    }
}